import { useCallback, useEffect, useState } from 'react';

import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';

/**
 * Hook de onboarding de bienvenida (Anexo E · E6).
 *
 * Persiste el estado de "ya vio la bienvenida" del empleado en
 * `core.user_onboarding` (RLS own). La tabla puede no existir todavía
 * si la migración no se aplicó — por eso TODO acceso a DB cae con
 * DEGRADACIÓN GRACEFUL a localStorage. Nunca lanza un throw que tumbe
 * la app: cada operación está envuelta en try/catch.
 *
 * - Sin sesión → `welcomeDismissed = true` (no mostramos nada).
 * - Mientras se resuelve el user id → `loading = true`.
 * - El acceso a `core.*` va SIEMPRE con `.schema('core')`.
 */

interface OnboardingLocalState {
  welcome_dismissed?: boolean;
  completed_tours?: string[];
}

function localStorageKey(uid: string): string {
  return `dendev_onboarding_${uid}`;
}

function readLocal(uid: string): OnboardingLocalState {
  try {
    const raw = window.localStorage.getItem(localStorageKey(uid));
    if (!raw) return {};
    return JSON.parse(raw) as OnboardingLocalState;
  } catch {
    return {};
  }
}

function writeLocal(uid: string, patch: OnboardingLocalState): void {
  try {
    const current = readLocal(uid);
    const next = { ...current, ...patch };
    window.localStorage.setItem(localStorageKey(uid), JSON.stringify(next));
  } catch {
    // localStorage puede fallar (modo privado, cuota). Degradamos en
    // silencio: el peor caso es que la bienvenida se vuelva a mostrar.
  }
}

interface UseOnboardingResult {
  welcomeDismissed: boolean;
  loading: boolean;
  dismissWelcome: () => Promise<void>;
}

export function useOnboarding(): UseOnboardingResult {
  const { user, loading: sessionLoading } = useSupabase();
  const uid = user?.id ?? null;

  // Arranca dismissed=true para no parpadear el modal antes de resolver.
  const [welcomeDismissed, setWelcomeDismissed] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      // Mientras la sesión no terminó de cargar, seguimos en loading.
      if (sessionLoading) {
        setLoading(true);
        return;
      }

      // Sin usuario autenticado → no mostramos onboarding.
      if (!uid) {
        if (!cancelled) {
          setWelcomeDismissed(true);
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      // localStorage es SIEMPRE fuente de verdad de respaldo: si acá ya
      // está marcado como visto, no mostramos el cartel pase lo que pase con
      // la DB. Esto evita el loop "siempre reaparece" cuando la tabla
      // core.user_onboarding todavía no existe o la consulta no marca error
      // de forma limpia. (Bug Anexo E · E6, corregido 2026-06-01.)
      const localDismissed = Boolean(readLocal(uid).welcome_dismissed);
      if (localDismissed) {
        if (!cancelled) {
          setWelcomeDismissed(true);
          setLoading(false);
        }
        return;
      }

      // Si localStorage no lo tiene, intento la tabla core.user_onboarding
      // (fuente central · cross-device cuando se aplique la migración E6).
      //
      // ⚠️ La tabla no figura en database.types.ts hasta que se aplique la
      // migración E6 y se regeneren los tipos. Mientras tanto casteamos a
      // `any` (mismo patrón que features/settings/my-ai/api.ts) para no
      // romper el build.
      try {
        const { data, error } = await supabase
          .schema('core')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from('user_onboarding' as any)
          .select('welcome_dismissed, completed_tours')
          .eq('user_id', uid)
          .maybeSingle();

        if (error) throw error;

        if (!cancelled) {
          const row = data as { welcome_dismissed?: boolean } | null;
          const dbDismissed = Boolean(row?.welcome_dismissed);
          // Si la DB dice que ya lo vio, lo replicamos en localStorage para
          // no volver a consultar y cortar el loop en este dispositivo.
          if (dbDismissed) writeLocal(uid, { welcome_dismissed: true });
          setWelcomeDismissed(dbDismissed);
          setLoading(false);
        }
        return;
      } catch {
        // La tabla puede no existir aún, o hubo error de red. Sin fila local
        // ni DB → todavía no vio la bienvenida (mostramos el cartel una vez).
        if (!cancelled) {
          setWelcomeDismissed(false);
          setLoading(false);
        }
      }
    }

    void resolve();

    return () => {
      cancelled = true;
    };
  }, [uid, sessionLoading]);

  const dismissWelcome = useCallback(async () => {
    // Cerramos el modal de inmediato (estado local optimista).
    setWelcomeDismissed(true);

    if (!uid) return;

    // Persistimos en localStorage SIEMPRE (no solo si la DB falla): es la
    // garantía de que el cartel no reaparezca en este dispositivo aunque la
    // tabla central no exista o el upsert no marque error. (Fix Anexo E · E6.)
    writeLocal(uid, { welcome_dismissed: true });

    // Además intento persistir en core.user_onboarding (central · cross-device
    // cuando se aplique la migración E6). Si falla, ya quedó en localStorage.
    try {
      await supabase
        .schema('core')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('user_onboarding' as any)
        .upsert(
          { user_id: uid, welcome_dismissed: true },
          { onConflict: 'user_id' },
        );
    } catch {
      // Silencioso: el flag ya está en localStorage.
    }
  }, [uid]);

  return { welcomeDismissed, loading, dismissWelcome };
}
